import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LocalService } from './storage/local.service';
import { AuthService } from './auth/auth.service';
import { TimeoutLoggingService } from './auth/timeout-logging.service';
import environment from '../../environments/environment';
import moment from 'moment-timezone';
import { Router } from '@angular/router';

export interface IdleTimeoutDialogOutput {
    keepAlive: boolean;
    preserveLocation: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class IdleTimeoutService
{
    private idleTimeoutInterval: NodeJS.Timer = null;
    private idleTimeoutCountdownSubject$: BehaviorSubject<number> = new BehaviorSubject<number>(environment.IdleTimeoutCountdown);
    private readonly openSubject$ = new BehaviorSubject<boolean>(false);
    private readonly idleTimeoutThreshold = environment.IdleTimeoutThreshold;

    /** Whether the session-expiry dialog is showing; the root app component hosts it. */
    readonly open$ = this.openSubject$.asObservable();
    readonly countdown$ = this.idleTimeoutCountdownSubject$.asObservable();

    constructor(
            private _authService: AuthService,
            private _router: Router,
            private _timeoutLogging: TimeoutLoggingService,
    ) {
    }

    get currentValue() {
        return this.idleTimeoutCountdownSubject$.getValue();
    }

    get countdown() {
        const stored = +LocalService.get('payhoa-idle-timeout');
        // increment difference +1 to progress countdown value
        const difference = moment().diff(stored, 'seconds') + 1;

        return environment.IdleTimeoutCountdown - difference;
    }

    watch() {
        if (this.idleTimeoutInterval !== null) {
            return;
        }
        this.idleTimeoutInterval = setInterval(() => {
            // kill the loop if we're on the auth layout without being stopped naturally
            if (this._router.url.indexOf('/auth/') > -1) {
                return this.stop();
            }

            if (LocalService.get('payhoa-idle-timeout') === null) {
                LocalService.set('payhoa-idle-timeout', moment.now().toString());
            }

            if (this.countdown < 1) {
                const output: IdleTimeoutDialogOutput = {
                    keepAlive: false,
                    preserveLocation: true,
                };

                if (this.isOpen) {
                    this.close(output);
                } else {
                    this.handleIdleTimedOut(output);
                }
            } else if (!this.isOpen && this.countdown <= this.idleTimeoutThreshold) {
                this.openSubject$.next(true);
            }

            this.idleTimeoutCountdownSubject$.next(this.countdown);

            // another tab reset the shared timer, so this tab's warning closes without signing out
            this.dialogCheck();
        }, 1000);
    }

    /** Close the session-expiry dialog with the person's choice (or the timer's). */
    close(output: IdleTimeoutDialogOutput) {
        if (!this.isOpen) {
            return;
        }
        this.openSubject$.next(false);
        this.handleIdleTimedOut(output);
    }

    private get isOpen(): boolean {
        return this.openSubject$.getValue();
    }

    reset() {
        this.openSubject$.next(false);
        LocalService.set('payhoa-idle-timeout', moment.now().toString());
        this.idleTimeoutCountdownSubject$.next(environment.IdleTimeoutCountdown);
    }

    stop() {
        this.openSubject$.next(false);
        clearInterval(this.idleTimeoutInterval);
        this.idleTimeoutInterval = null;
        LocalService.destroy('payhoa-idle-timeout');
    }

    // on document:visibilitychange in browser, if keepAlive check passes, close dialog and resume countdown
    dialogCheck() {
        if (this.isOpen && this.countdown > this.idleTimeoutThreshold) {
            this.close({keepAlive: true, preserveLocation: false});
        }
    }

    private handleIdleTimedOut(result: IdleTimeoutDialogOutput): void {
        if (result?.keepAlive) {
            // any endpoint to refresh token on backend too
            this._authService.keepAlive().subscribe();

            return this.reset();
        }

        const idleDuration = environment.IdleTimeoutCountdown - this.countdown;
        this._timeoutLogging.logIdleTimeout(idleDuration).subscribe();
        this.stop();
        this._authService.logout(result?.preserveLocation);
    }
}
